import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_URL } from '../api';

export const AuthContext = createContext(null);

const DEFAULT_TIMEOUT_MS = 10_000;

function toTimeoutError(url, timeoutMs, originalError) {
  const err = new Error(
    'Servern svarar inte — det kan bero på hög belastning eller att tjänsten startar om. Försök igen om en stund.'
  );
  err.name = 'TimeoutError';
  err.cause = originalError;
  return err;
}

function toNetworkError(url, originalError) {
  const err = new Error(
    'Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen. Om problemet kvarstår kan tjänsten vara tillfälligt nere.'
  );
  err.name = 'NetworkError';
  err.cause = originalError;
  return err;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId = null;

  try {
    if (controller && timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        try {
          controller.abort();
        } catch {
          // ignore
        }
      }, timeoutMs);
    }

    const opts = controller ? { ...(options || {}), signal: controller.signal } : (options || {});

    try {
      return await fetch(url, opts);
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw toTimeoutError(url, timeoutMs, err);
      }
      // Most browsers throw TypeError("Failed to fetch") on network/CORS issues.
      if (err instanceof TypeError) {
        throw toNetworkError(url, err);
      }
      throw err;
    }
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keepaliveId, setKeepaliveId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetchWithTimeout(`${API_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
      return r.ok;
    } catch {
      return false;
    }
  }, []);

  const fetchMe = useCallback(async () => {
    // VIKTIGT: bara ett definitivt 401/403 far logga ut anvandaren.
    // Timeout/natverksfel betyder "servern ar seg just nu" — inte "sessionen
    // ar ogiltig". Tidigare kastades anvandare till login-sidan sa fort
    // /users/me tog >10 s (t.ex. under inloggningsbursten pa stage), trots
    // giltig session. Pilotfynd 2026-08-06.
    try {
      let res;
      try {
        res = await fetchWithTimeout(`${API_URL}/users/me`, { credentials: 'include' });
      } catch (err) {
        if (err?.name === 'TimeoutError' || err?.name === 'NetworkError') {
          // Ett omforsok med langre tidsgrans — servern kan sta mitt i en
          // inloggningsburst. Battre an att visa login-sidan i onodan.
          res = await fetchWithTimeout(`${API_URL}/users/me`, { credentials: 'include' }, 25_000);
        } else {
          throw err;
        }
      }
      if (res.status === 401) {
        // Access expired? Try refresh then retry once.
        const ok = await refresh();
        if (ok) {
          res = await fetchWithTimeout(`${API_URL}/users/me`, { credentials: 'include' });
        }
      }
      if (res.ok) {
        const u = await res.json();
        setUser(u);
      } else if (res.status === 401 || res.status === 403) {
        setUser(null);
      }
      // Ovriga statusar (5xx, 429): behall nuvarande sessionstillstand —
      // ett tillfalligt serverfel ska inte trigga utloggning.
    } catch {
      // Timeout/natverksfel: behall nuvarande anvandare (om nagon).
      // Vid kall sidladdning ar user redan null → login-sidan visas anda.
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  // Initial session check
  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Background keepalive while logged in: refresh every ~20 minutes
  useEffect(() => {
    if (user && !keepaliveId) {
      const id = setInterval(() => { refresh(); }, 20 * 60 * 1000);
      setKeepaliveId(id);
      return () => { clearInterval(id); };
    }
    if (!user && keepaliveId) {
      clearInterval(keepaliveId);
      setKeepaliveId(null);
    }
  }, [user, keepaliveId, refresh]);

  const login = useCallback(async (email, password) => {
    const res = await fetchWithTimeout(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    }, 30_000);
    if (!res.ok) {
      let msg = '';
      try {
        const body = await res.text();
        // Try to extract a JSON detail message from FastAPI
        try {
          const json = JSON.parse(body);
          msg = json.detail || json.message || '';
        } catch {
          msg = body;
        }
      } catch { /* ignore */ }

      if (res.status === 401 || res.status === 403) {
        throw new Error(msg || 'Fel e-postadress eller lösenord. Kontrollera uppgifterna och försök igen.');
      }
      if (res.status === 429) {
        throw new Error('För många inloggningsförsök. Vänta en stund och försök igen.');
      }
      if (res.status >= 500) {
        throw new Error('Ett serverfel uppstod. Försök igen om en stund eller kontakta support om problemet kvarstår.');
      }
      throw new Error(msg || 'Inloggningen misslyckades. Försök igen.');
    }
    const u = await res.json();

    // Tvafaktor: losenordet stammer men ingen session ar utfardad an. Vi satter
    // INTE user har — anroparen (inloggningssidan) tar hand om steg tva.
    if (u && u.mfa_required) {
      return u;
    }

    setUser(u);
    // Login response is intentionally minimal; fetch full profile (e.g. avatar_url).
    try {
      await fetchMe();
    } catch {
      // ignore; keep minimal login user
    }
    return u;
  }, [fetchMe]);

  /**
   * Steg tva i inloggningen: vaxla utmaningstoken + kod mot en session.
   *
   * @param {string} challenge Utmaningstoken fran /auth/login
   * @param {string} code Engangskod eller reservkod
   * @param {boolean} [useRecovery] true = reservkod i stallet for appkod
   */
  const verifyMfa = useCallback(async (challenge, code, useRecovery = false) => {
    const path = useRecovery ? '/auth/mfa/recovery' : '/auth/mfa/verify';
    const res = await fetchWithTimeout(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ challenge, code })
    }, 30_000);

    if (!res.ok) {
      let msg = '';
      try {
        const body = await res.text();
        try {
          const json = JSON.parse(body);
          msg = json.detail || json.message || '';
        } catch {
          msg = body;
        }
      } catch { /* ignore */ }
      // Backend skiljer pa fel kod och redan anvand kod — det beskedet ska na
      // fram oforandrat, annars letar anvandaren efter ett fel som inte finns.
      throw new Error(msg || 'Koden kunde inte verifieras. Försök igen.');
    }

    const u = await res.json();
    setUser(u);
    try {
      await fetchMe();
    } catch {
      // ignore
    }
    return u;
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await fetchWithTimeout(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // ignore
    }
    // Allt användarspecifikt lokalt state rensas vid utloggning, så att nästa
    // inloggning på samma webbläsare inte ärver föregående användares
    // favoriter, tenant-roll, dashboard-layout, modullista eller simulering.
    // Kvarlämnad tenant-roll gav fel UI-gating vid användarbyte (fynd 2026-08-06).
    try {
      const userScopedKeys = [
        'kp-favorites',
        'kp-recent-items',
        'kp-collapsed-sections',
        'kanplan-active-tenant-slug',
        'kanplan-active-tenant-role',
        'kanplan-active-tenant-name',
        'kanplan-simulated-role',
        'kanplan-simulated-user',
        'kp-modules-cache',
        'kanplan-field-visible-screens',
        'kp_dashboard_view',
        'kp_dashboard_widgets',
        'kp_dashboard_widget_order',
        'kanplan-inkop-recent-suppliers',
        'kanplan-inkop-offer-weights',
      ];
      for (const k of userScopedKeys) localStorage.removeItem(k);
    } catch {}
    setUser(null);
  }, []);

  const value = { user, loading, login, logout, verifyMfa, refreshMe: fetchMe };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
